<template>
    <DataTable
        ref="dataTable"
        :loading="isLoading"
        :value="entities"
        selection-mode="single"
        dataKey="identity"
        :rowHover="true"
        :rowEditor="isEditing"
        @row-click="
            (event: DataTableRowClickEvent) => {
                const rowLink = `entity-link-${event.data.identity}`;
                dataTable.$el.querySelector(`#${rowLink}`)?.click();
            }
        "
    >
        <template #empty> No entities found. </template>
        <template #loading> Fetching data... </template>
        <template #header>
            <div style="display: flex; justify-content: space-between">
                <h2>Definitions</h2>
                <Button
                    v-if="props.isEditing"
                    label="Add definition"
                    icon="pi pi-plus"
                    text
                    size="small"
                    severity="secondary"
                    @click="openEditEntity()"
                />
            </div>
        </template>
        <Column field="name" header="Name" class="name-col">
            <template #body="{ data, field }">
                <RouterLink
                    :id="`entity-link-${data.identity}`"
                    class="entity-link"
                    :to="{
                        name: 'businessEntityPage',
                        params: {
                            orgName: route.params.orgName,
                            stekName: route.params.stekName,
                            businessEntityId: data.identity,
                        },
                    }"
                >
                    <div>
                        {{ data[field] }}
                    </div>
                </RouterLink>
            </template>
        </Column>
        <Column field="short_description" header="Description">
            <template #body="slotProps">
                <template v-if="slotProps.data.short_description">
                    {{ slotProps.data.short_description }}
                </template>
                <span
                    v-else
                    v-tooltip.top="'A description is not (yet) available'"
                >
                    ...
                </span>
            </template>
        </Column>
        <Column field="" header="" class="buttons-col">
            <template #body="{ index, data }" v-if="isEditing">
                <Button
                    severity="secondary"
                    type="button"
                    text
                    rounded
                    icon="pi pi-pencil"
                    :loading="isLoading"
                    @click="
                        () => {
                            openEditEntity(data);
                        }
                    "
                />
                <Button
                    severity="danger"
                    type="button"
                    text
                    rounded
                    icon="pi pi-trash"
                    :loading="isLoading"
                    @click="
                        (e: MouseEvent) => {
                            // Using stopPropagation will break confirm: https://github.com/primefaces/primevue/issues/5706
                            deleteBusinessEntity(e, index);
                        }
                    "
                />
            </template>
        </Column>
    </DataTable>
</template>

<script setup lang="ts">
import { useBusinessEntitiesStore } from "@/stores/business-entities-store";
import { storeToRefs } from "pinia";
import { useConfirm } from "primevue/useconfirm";
import { useEditEntityDialog } from "@/composables/edit-entity-dialog";
import { DataTableRowClickEvent } from "primevue/datatable";
import { ref } from "vue";
import { useRoute } from "vue-router";

const props = defineProps({ isEditing: Boolean });

const dataTable = ref();
const route = useRoute();

const { entities, isLoading } = storeToRefs(useBusinessEntitiesStore());
const { deleteEntity } = useBusinessEntitiesStore();

const { open: openEditEntity } = useEditEntityDialog();

const confirm = useConfirm();

const deleteBusinessEntity = async (event: Event, index: number) => {
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: `
            After deletion of '${entities.value[index].name}'
            as definition, you cannot reference it anymore. Are you sure?
        `,
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            await deleteEntity(entities.value[index].identity);
        },
    });
};
</script>

<style scoped lang="scss">
.entity-link {
    text-decoration: none;
}

:deep(.p-datatable-thead) > tr > th,
:deep(.p-datatable-tbody) > tr > td {
    &.buttons-col {
        min-width: 50px;
        text-align: right;
    }
    &.name-col {
        width: 30rem;
    }
}
:deep(.p-datatable-tbody) > tr > td.buttons-col {
    padding-top: 0;
    padding-bottom: 0;
}
</style>
